/**
 * PWA Checker Utility
 * Validates that PWA is properly configured and working
 */

/**
 * Checks if Service Worker is registered and active
 * @returns {Promise<Object>} - { registered: boolean, active: boolean, error: string }
 */
export async function checkServiceWorker() {
  try {
    if (!('serviceWorker' in navigator)) {
      return {
        registered: false,
        active: false,
        error: 'Service Worker is not supported in this browser'
      };
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const activeWorker = navigator.serviceWorker.controller;

    return {
      registered: registrations.length > 0,
      active: !!activeWorker,
      registrationsCount: registrations.length,
      activeWorker: activeWorker ? 'Active' : 'None'
    };
  } catch (error) {
    return {
      registered: false,
      active: false,
      error: error.message
    };
  }
}

/**
 * Checks if manifest.json exists and is valid
 * @returns {Promise<Object>} - { exists: boolean, valid: boolean, data: Object, error: string }
 */
export async function checkManifest() {
  try {
    const response = await fetch('/manifest.json');
    
    if (!response.ok) {
      return {
        exists: false,
        valid: false,
        error: `Manifest not found: ${response.status} ${response.statusText}`
      };
    }

    const manifest = await response.json();
    
    // Validate required fields
    const requiredFields = ['name', 'short_name', 'icons', 'start_url', 'display'];
    const missingFields = requiredFields.filter(field => !manifest[field]);
    
    // Validate icons
    const hasIcons = manifest.icons && Array.isArray(manifest.icons) && manifest.icons.length > 0;
    const hasRequiredIconSizes = manifest.icons?.some(icon => 
      icon.sizes === '192x192' || icon.sizes === '512x512'
    );

    return {
      exists: true,
      valid: missingFields.length === 0 && hasIcons && hasRequiredIconSizes,
      data: manifest,
      missingFields,
      hasIcons,
      hasRequiredIconSizes,
      iconsCount: manifest.icons?.length || 0
    };
  } catch (error) {
    return {
      exists: false,
      valid: false,
      error: error.message
    };
  }
}

/**
 * Checks if the app is running in standalone mode (installed)
 * @returns {Object} - { installed: boolean, displayMode: string }
 */
export function checkInstallationStatus() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = window.navigator.standalone === true;
  const isAndroidApp = document.referrer.includes('android-app://');
  
  const installed = isStandalone || isIOSStandalone || isAndroidApp;
  
  let displayMode = 'browser';
  if (isStandalone) displayMode = 'standalone';
  else if (isIOSStandalone) displayMode = 'ios-standalone';
  else if (isAndroidApp) displayMode = 'android-app';

  return {
    installed,
    displayMode,
    isStandalone,
    isIOSStandalone,
    isAndroidApp
  };
}

/**
 * Checks if HTTPS is being used (required for PWA)
 * @returns {Object} - { isSecure: boolean, protocol: string, isLocalhost: boolean }
 */
export function checkSecurity() {
  const protocol = window.location.protocol;
  const isHTTPS = protocol === 'https:';
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname === '[::1]';
  const isSecure = isHTTPS || isLocalhost;

  return {
    isSecure,
    protocol,
    isLocalhost,
    hostname: window.location.hostname
  };
}

/**
 * Checks if required PWA icons exist
 * @returns {Promise<Object>} - { icons: Array, missing: Array }
 */
export async function checkPWAIcons() {
  const requiredIcons = ['Logo.png'];
  const iconResults = [];

  for (const icon of requiredIcons) {
    try {
      const response = await fetch(`/${icon}`, { method: 'HEAD' });
      iconResults.push({
        name: icon,
        exists: response.ok,
        status: response.status
      });
    } catch (error) {
      iconResults.push({
        name: icon,
        exists: false,
        error: error.message
      });
    }
  }

  const missing = iconResults.filter(icon => !icon.exists);

  return {
    icons: iconResults,
    missing,
    allExist: missing.length === 0
  };
}

/**
 * Comprehensive PWA check - runs all checks
 * @returns {Promise<Object>} - Complete PWA status report
 */
export async function runPWACheck() {
  const [serviceWorker, manifest, installation, security, icons] = await Promise.all([
    checkServiceWorker(),
    checkManifest(),
    Promise.resolve(checkInstallationStatus()),
    Promise.resolve(checkSecurity()),
    checkPWAIcons()
  ]);

  const report = {
    timestamp: new Date().toISOString(),
    serviceWorker,
    manifest,
    installation,
    security,
    icons,
    overall: {
      passed: serviceWorker.registered && 
              manifest.valid && 
              security.isSecure && 
              icons.allExist,
      warnings: [],
      errors: []
    }
  };

  // Collect warnings and errors
  if (!serviceWorker.registered) {
    report.overall.warnings.push('Service Worker is not registered');
  }
  if (!serviceWorker.active) {
    report.overall.warnings.push('Service Worker is not active');
  }
  if (!manifest.valid) {
    report.overall.errors.push('Manifest.json is invalid or missing required fields');
  }
  if (!security.isSecure && !security.isLocalhost) {
    report.overall.errors.push('PWA requires HTTPS (or localhost)');
  }
  if (!icons.allExist) {
    report.overall.errors.push(`Missing PWA icons: ${icons.missing.map(i => i.name).join(', ')}`);
  }

  return report;
}

/**
 * Quick PWA status check (lightweight)
 * @returns {Object} - Quick status
 */
export function quickPWACheck() {
  const installation = checkInstallationStatus();
  const security = checkSecurity();
  
  return {
    installed: installation.installed,
    secure: security.isSecure,
    displayMode: installation.displayMode
  };
}
